
const API = process.env.REACT_APP_API || "";

export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export interface PinRequest {
  center: [number, number];
  label: string;
}

export interface Pin extends PinRequest {
  id: string;
}

export const postPin = async (pin: PinRequest, token: string): Promise<Pin> => {
  const response = await fetch(`${API}/pins/pin`, {
    method: "POST",
    body: JSON.stringify(pin),
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("Failed to post pin");
  }
  return await response.json();
};

export const getPins = async (token: string): Promise<Pin[]> => {
  const response = await fetch(`${API}/pins`, {
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch pins");
  }
  const data = await response.json();
  return data.pins;
};

export const deletePin = async (id: string, token: string): Promise<void> => {
  const response = await fetch(`${API}/pins/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("Failed to delete pin");
  }
};

export interface SpotRequest {
  pins: Pin[];
  searchRadiusMiles: number;
}

export interface Place {
  name: string;
  address: string;
  location: { lat: number; lng: number };
}

export interface SpotResponse {
  geojson: GeoJSON.LineString;
  center: GeoJSON.Point;
  places: Place[];
}

export const searchSpots = async (request: SpotRequest, token: string): Promise<SpotResponse> => {
  const response = await fetch(`${API}/spots/search`, {
    method: "POST",
    body: JSON.stringify(request),
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new UnauthorizedError("Unauthorized");
    }
    throw new Error("Failed to search spots");
  }
  return await response.json();
};

export interface User {
  userId: string;
  email: string;
  name: string;
  createdAt: Date;
}

export const signUp = async (token: string): Promise<User> => {
  const response = await fetch(`${API}/users/signup`, {
    method: "POST",
    body: JSON.stringify({ token }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Failed to sign up");
  }
  return await response.json();
};

export const getMe = async (token: string): Promise<User> => {
  const response = await fetch(`${API}/users/me`, {
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    }
  });
  if (!response.ok) {
    throw new Error("Failed to fetch user");
  }
  return await response.json();
};
